import { createApi, fetchBaseQuery }                                                 from "@reduxjs/toolkit/query/react";
import { ENDPOINTS }                                                                 from "src/constants/api/api";
import { getAccessToken }                                                            from "src/utils/token-storage";
import type { ILoginRequest, IRegisterRequest }                                      from "src/store/services/auth/interfaces/requests.interface";
import type { ILoginResponse, ILogoutResponse, IRefreshResponse, IRegisterResponse } from "src/store/services/auth/interfaces/responses.interface";

type GoogleCalendarStatusResponse = {
  connected  : boolean;
  email      : string | null;
  connectedAt: string | null;
};

type GoogleCalendarConnectUrlResponse = {
  url: string;
};

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery  : fetchBaseQuery({
    baseUrl       : `${process.env.REACT_APP_BASE_URL}`,
    credentials   : "include",
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  }),
  tagTypes : ["Auth"],
  endpoints: (builder) => ({
    login: builder.mutation<ILoginResponse, ILoginRequest>({
      query: (credentials) => ({
        url   : ENDPOINTS.AUTH.LOGIN,
        method: "POST",
        body  : credentials,
      }),
    }),
    logout: builder.mutation<ILogoutResponse, void>({
      query: () => ({
        url   : ENDPOINTS.AUTH.LOGOUT,
        method: "POST",
      }),
    }),
    refresh: builder.mutation<IRefreshResponse, void>({
      query: () => ({
        url        : ENDPOINTS.AUTH.REFRESH,
        method     : "POST",
        credentials: "include",
      }),
    }),
    register: builder.mutation<IRegisterResponse, IRegisterRequest>({
      query: (registrationData) => ({
        url   : ENDPOINTS.AUTH.REGISTER,
        method: "POST",
        body  : registrationData,
      }),
    }),
    stopImpersonation: builder.mutation<ILoginResponse, void>({
      query: () => ({
        url   : ENDPOINTS.AUTH.STOP_IMPERSONATION,
        method: "POST",
      }),
    }),
    getGoogleCalendarStatus: builder.query<GoogleCalendarStatusResponse, void>({
      query: () => ({
        url   : ENDPOINTS.AUTH.GOOGLE_CALENDAR_STATUS,
        method: "GET",
      }),
      providesTags: ["Auth"],
    }),
    getGoogleCalendarConnectUrl: builder.query<GoogleCalendarConnectUrlResponse, { backUrl: string }>({
      query: ({ backUrl }) => ({
        url   : ENDPOINTS.AUTH.GOOGLE_CALENDAR_CONNECT_URL,
        method: "GET",
        params: { backUrl },
      }),
    }),
  }),
});

export const { useGetGoogleCalendarStatusQuery,
  useLazyGetGoogleCalendarConnectUrlQuery,
  useLoginMutation,
  useLogoutMutation,
  useRefreshMutation,
  useRegisterMutation,
  useStopImpersonationMutation } = authApi;
